import classNames from "classnames";
import {runInAction} from "mobx";
import {observer} from "mobx-react-lite";
import React from "react";
import {UncontrolledTooltip} from "reactstrap";
import {IconSource} from "../../../shared/enums/iconSource";
import {ObservableList} from "../../../stores/observables/observableList";
import {RadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/radioGroupKeyValue";
import Icon from "../common/icon/icon";
import "./radioGroup.sass";
import {LayoutOrientation} from "../../../shared/enums/layoutOrientation";

export interface IRadioGroupProps {
  // The list of values the user can select from
  items: ObservableList<RadioGroupKeyValue>;

  // The name of the group, which must be unique per page
  name: string;

  // Is the component currently disabled
  isDisabled?: boolean;

  // Callback for when an item is selected
  onSelect?: (item: RadioGroupKeyValue) => void;

  // How the items will layout
  orientation?: "list" | "grid";
}

const RadioGroup: React.FC<IRadioGroupProps> = ({
  items,
  name,
  isDisabled = false,
  onSelect,
  orientation = "list"
}: IRadioGroupProps) => {
  const handleClick = (item: RadioGroupKeyValue): void => {
    runInAction(() => {
      items.selectedItem = item;
    });
    onSelect?.(item);
  };

  return (
    <div
      data-testid="radio-group"
      className={classNames("radio-group", {
        vertical: orientation === "list"
      })}
    >
      {items.items.map((item) => {
        const id = `${name}-${item.value}`; // prefix all ids with the radiogroup name
        const itemIsSelected = item.value === items.selectedItem?.value;
        return (
          <div
            className={classNames("custom-control custom-radio", {
              vertical: item.orientation === LayoutOrientation.List
            })}
            key={item.value}
          >
            <div>
              <input
                onChange={() => {
                  handleClick(item);
                }}
                id={id}
                type="radio"
                name={name}
                checked={itemIsSelected}
                disabled={isDisabled || item.isDisabled}
                className="custom-control-input"
              />
              <label
                htmlFor={id}
                className={classNames(
                  "custom-control-label",
                  {selected: itemIsSelected},
                  {disabled: isDisabled || item.isDisabled}
                )}
              >
                {item.label}
                {item.helpText && (
                  <span>
                    <small>{item.helpText}</small>
                  </span>
                )}
              </label>
              {item.hasTooltip && getTooltip(item)}
            </div>
            <div>
              {item.hasCustomInput &&
                item.renderCustomInput(isDisabled || !itemIsSelected)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function getTooltip(item: RadioGroupKeyValue): JSX.Element {
  const tooltipId = `tooltip-${item.value}`; // Scope the tooltip to the icon
  return (
    <>
      <span id={tooltipId}>
        <Icon source={IconSource.help} />
      </span>
      <UncontrolledTooltip target={tooltipId}>
        {item.tooltip}
      </UncontrolledTooltip>
    </>
  );
}

export default observer(RadioGroup);
